<template>
  <div class="py-8 px-6">
    <v-row
      no-gutters
      class="mb-4"
    >
      <v-col cols="auto">
        <v-checkbox
          v-model="taskDirty.included"
          class="ma-0 pa-0"
          hide-details
        />
      </v-col>
      <v-col>
        Task ID #{{ taskDirty.id }} &middot; Finished <strong>{{ taskDirty.finished_at | moment('D MMM YYYY') }}</strong>
        <v-chip
          v-if="taskDirty.deposit_insurance"
          label
          outlined
          small
          class="warning--text ml-1"
        >
          Deposit Insurance
        </v-chip>
        <v-chip
          v-else-if="taskDirty.hull_insurance"
          label
          outlined
          small
          class="warning--text ml-1"
        >
          Hull Insurance
        </v-chip>

        <v-chip
          v-if="taskDirty.is_warranty_claim"
          label
          outlined
          small
          class="ml-2"
        >
          Warranty Claim:
          <span
            v-if="taskDirty.warranty_claim_resolution === null"
            class="ml-1"
          >
            Pending
          </span>
          <span
            v-else-if="taskDirty.warranty_claim_resolution === true"
            class="success--text ml-1"
          >
            Accepted
          </span>
          <span
            v-else
            class="error--text ml-1"
          >
            Rejected
          </span>
          <span
            v-if="taskDirty.warranty_claim_resolution_at"
            class="ml-1"
          >
            ({{ taskDirty.warranty_claim_resolution_at | moment('D MMM YYYY') }})
          </span>
        </v-chip>
      </v-col>
    </v-row>
    <div>
      <v-text-field
        :value="taskDirty.title"
        outlined
        hide-details
        dense
      />
    </div>
    <div class="mt-1">
      <v-textarea
        v-model="taskDirty.description"
        class="caption"
        placeholder="Add description"
        hide-details
        single-line
        rows="2"
        outlined
        dense
      />
    </div>
    <div
      v-if="taskDirty.is_warranty_claim"
      class="mt-3"
    >
      <div>
        Warranty / Insurance Note
      </div>
      <v-textarea
        v-model="taskDirty.warranty_insurance_note"
        class="caption"
        placeholder="Add warranty / insurance note"
        hide-details
        single-line
        rows="2"
        outlined
        dense
      />
    </div>

    <div class="font-weight-bold mt-3 mb-1">
      Items
    </div>

    <v-row
      v-for="(item, index) in taskDirty.items"
      :key="index"
    >
      <v-col cols="auto">
        <v-checkbox
          v-model="item.included"
          class="ma-0 pa-0 pt-2"
          hide-details
          @change="recalculateItem(index)"
        />
      </v-col>
      <v-col>
        <validation-provider
          v-slot="{ errors }"
          rules="required"
        >
          <v-text-field
            v-model="item.name"
            label="Item Label"
            outlined
            hide-details
            dense
            :error-messages="errors"
          />
        </validation-provider>
      </v-col>
      <v-col cols="auto">
        <validation-provider
          v-slot="{ errors }"
          rules="required|min_value:0.1"
        >
          <v-text-field
            v-model="item.quantity"
            label="Qty"
            outlined
            hide-details
            dense
            style="width: 100px"
            type="number"
            step="0.1"
            :error-messages="errors"
            @change="recalculateItem(index)"
          />
        </validation-provider>
      </v-col>
      <v-col cols="auto">
        <validation-provider
          v-slot="{ errors }"
        >
          <v-text-field
            v-model="item.unit"
            label="Unit"
            outlined
            hide-details
            dense
            :error-messages="errors"
            style="width: 100px"
          />
        </validation-provider>
      </v-col>
      <v-col cols="auto">
        <validation-provider
          v-slot="{ errors }"
          rules="required"
        >
          <v-text-field
            v-model="item.unit_price"
            :suffix="currency"
            style="width: 140px"
            autocomplete="disable"
            label="Unit Price"
            outlined
            hide-details
            dense
            type="number"
            :error-messages="errors"
            @change="recalculateItem(index)"
          />
        </validation-provider>
      </v-col>
      <v-col cols="auto">
        <v-text-field
          :value="item.price"
          :suffix="currency"
          style="width: 140px"
          label="Price"
          solo
          hide-details
          type="number"
          dense
          readonly
        />
      </v-col>
    </v-row>
    <v-row align="center">
      <v-col>
        <v-btn
          color="secondary"
          small
          text
          class="text-none"
          @click="addItem()"
        >
          <v-icon
            left
            x-small
          >
            fa fa-plus
          </v-icon>
          Add item
        </v-btn>
      </v-col>
      <v-col class="text-right font-weight-medium">
        Subtotal
      </v-col>
      <v-col cols="auto">
        <v-text-field
          v-model="taskDirty.price"
          :suffix="currency"
          style="width: 140px"
          outlined
          hide-details
          dense
          type="number"
        />
      </v-col>
    </v-row>
  </div>
</template>
<script>
import Big from 'big.js'
import cloneDeep from 'lodash/cloneDeep'

export default {
  name: 'ServiceCostReportRow',
  props: {
    task: {
      type: Object,
      required: true
    },
    currency: {
      type: String,
      required: true
    }
  },
  computed: {
    taskDirty: {
      set(value) {
        this.$emit('update:task', value)
      },
      get() {
        return this.task
      }
    }
  },
  methods: {
    addItem() {
      this.taskDirty.items.push({
        custom: true,
        included: true,
        name: '',
        price: null,
        quantity: '',
        unit: '',
        unit_price: null
      })
    },
    recalculateItem(index) {
      const item = cloneDeep(this.taskDirty.items[index])

      if (!item.unit_price || !item.quantity) return

      item.price = Big(item.unit_price).times(item.quantity).toNumber()

      this.taskDirty.items.splice(index, 1, item)

      const calcItems = this.taskDirty.items.filter(item => item.included).map(item => item.price)

      this.taskDirty.price = calcItems.length > 0 ? Big(calcItems.reduce((a, b) => a + b)).toNumber() : 0
    }
  }
}
</script>
